import React, { useCallback, useEffect, useState } from "react";
import "../../App.css";

import TelegramText from "../../components/kit/Text/TelegramText";
import { useTelegram } from "../../hooks/useTelegram";
import { useNavigate } from "react-router-dom";
import {
  PATH_PRODUCT_TYPE,
  PATH_HOME,
  PATH_ORDER_MANAGEMENT,
} from "../../constants/Paths";
import TelegramScreen from "../../components/kit/Screen/TelegramScreen";
import ItemButton from "../../components/kit/ItemButton/ItemButton";
import TelegramHeader from "../../components/kit/Header/TelegramHeader";
import StoreSelectionCard from "../../components/kit/StoreSelectionCard/StoreSelectionCard";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/kit/Loader/Loader";
import { useAlert } from "../../components/providers/AlertProvider";
import { updateSelectedStore, clearCurrentTimeSlotOrders, updateCurrentTimeSlotOrders } from "../../components/kit/Redux/Actions";
import { getStores, updateUserStore, postToAPI, getStoreSpecificOrders, getStoreBalanceRecommendation } from "../../db/db";
import { useFullAuth } from "../../hooks/useAuthGuard";
import { useFullStateReloadHandler } from "../../hooks/usePageReloadHandler";
import ErrorHandler from "../../components/kit/ErrorHandler/ErrorHandler";

const StoreSelectionScreen = () => {
  const { webApp } = useTelegram();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerAlert } = useAlert();

  // Auth guard and reload handler
  const { isAuthenticated } = useFullAuth();
  useFullStateReloadHandler();

  const [isLoading, setIsLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showStoreSelection, setShowStoreSelection] = useState(false);
  const [error, setError] = useState(null);
  const [storeRecommendation, setStoreRecommendation] = useState(null);

  const sessionToken = useSelector((state) => state.sessionToken);
  const userProfile = useSelector((state) => state.userProfile);
  const currentTimeSlotOrders = useSelector(
    (state) => state.currentTimeSlotOrders
  );
  const currentSelectedStore = useSelector((state) => state.selectedStore);

  // Fetch stores from API using db.js function
  const fetchStores = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const storesData = await getStores(sessionToken);
      setStores(storesData);
    } catch (error) {
      console.error("Error fetching stores:", error);
      setError({
        ...error,
        title: "Store Loading Failed",
        subtitle: "Unable to load available stores",
        action: "fetchStores",
      });
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken]);

  // Handle store selection
  const onStoreSelect = useCallback(
    async (selectedStoreItem) => {
      try {
        setIsLoading(true);
        setError(null);
        setSelectedStore(selectedStoreItem);

        // Update user's store selection via db.js function
        const storeResponse = await updateUserStore(
          sessionToken,
          userProfile.userid,
          selectedStoreItem.id
        );

        // Update per-store max quantity config if returned
        if (storeResponse?.data?.maxQuantityConfig) {
          localStorage.setItem('maxQuantityConfig', JSON.stringify(storeResponse.data.maxQuantityConfig));
        }

        // Store selection saved successfully
        dispatch(
          updateSelectedStore({
            storeId: selectedStoreItem.id,
            storeCode: selectedStoreItem.code,
            storeName: selectedStoreItem.name || selectedStoreItem.title,
            storeEmoji: selectedStoreItem.emoji,
          })
        );

        // Clear existing orders since we're switching stores
        dispatch(clearCurrentTimeSlotOrders());

        // Check for store-specific orders
        console.log(`Checking for orders in store ${selectedStoreItem.id} for user ${userProfile.userid}`);
        const storeOrders = await getStoreSpecificOrders(
          sessionToken,
          selectedStoreItem.id,
          userProfile.userid
        );

        console.log(`Store-specific orders found:`, storeOrders);

        // Update current time slot orders with store-specific data
        dispatch(updateCurrentTimeSlotOrders(storeOrders));

        // Reset the selection state
        setShowStoreSelection(false);

        // Navigate based on whether orders exist for this store
        if (storeOrders && Object.keys(storeOrders).length > 0) {
          console.log(`Navigating to Order Management - found orders for store ${selectedStoreItem.id}`);
          navigate(PATH_ORDER_MANAGEMENT, { replace: true });
        } else {
          console.log(`Navigating to Product Type - no orders found for store ${selectedStoreItem.id}`);
          navigate(PATH_PRODUCT_TYPE, { replace: true });
        }
      } catch (error) {
        console.error("Error selecting store:", error);
        setError({
          ...error,
          title: "Store Selection Failed",
          subtitle: "Unable to save store selection",
          action: "storeSelect",
        });
      } finally {
        setIsLoading(false);
        setSelectedStore(null);
      }
    },
    [sessionToken, userProfile?.userid, navigate, dispatch]
  );

  // Handle Tree Change button click (show store selection)
  const onTreeChangeClick = useCallback(() => {
    setShowStoreSelection(true);
  }, []);

  // Handle main button click (continue with selected store)
  const onMainClick = useCallback(async () => {
    try {
      if (!currentSelectedStore) {
        navigate(PATH_PRODUCT_TYPE, { replace: true });
        return;
      }

      setIsLoading(true);
      setError(null);

      // Clear existing orders and reload store-specific orders
      dispatch(clearCurrentTimeSlotOrders());

      console.log(`Checking for orders in current store ${currentSelectedStore.storeId} for user ${userProfile.userid}`);
      const storeOrders = await getStoreSpecificOrders(
        sessionToken,
        currentSelectedStore.storeId,
        userProfile.userid
      );

      console.log(`Store-specific orders found for current store:`, storeOrders);

      // Update current time slot orders with store-specific data
      dispatch(updateCurrentTimeSlotOrders(storeOrders));

      // Navigate based on whether orders exist for this store
      if (storeOrders && Object.keys(storeOrders).length > 0) {
        console.log(`Navigating to Order Management - found orders for current store ${currentSelectedStore.storeId}`);
        navigate(PATH_ORDER_MANAGEMENT, { replace: true });
      } else {
        console.log(`Navigating to Product Type - no orders found for current store ${currentSelectedStore.storeId}`);
        navigate(PATH_PRODUCT_TYPE, { replace: true });
      }
    } catch (error) {
      console.error("Error checking store orders:", error);
      setError({
        ...error,
        title: "Order Check Failed",
        subtitle: "Unable to check for existing orders",
        action: "mainClick",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentSelectedStore, sessionToken, userProfile?.userid, navigate, dispatch]);

  const onBackClick = useCallback(() => {
    navigate(PATH_HOME, { replace: true });
  }, [navigate]);

  // Fetch store balance recommendation
  const fetchStoreRecommendation = useCallback(async () => {
    try {
      const recommendation = await getStoreBalanceRecommendation(sessionToken);
      setStoreRecommendation(recommendation);
      console.log("[StoreSelection] Store recommendation:", recommendation);
    } catch (error) {
      console.error("Error fetching store recommendation:", error);
      // Don't show error for recommendations - just continue without them
      setStoreRecommendation(null);
    }
  }, [sessionToken]);

  // Always force store selection - removed auto-loading of previous store
  const initializeStoreSelection = useCallback(() => {
    // Clear any existing store selection from Redux
    dispatch(updateSelectedStore({}));
    // Always show store selection interface
    setShowStoreSelection(true);
    // Fetch store balance recommendation
    fetchStoreRecommendation();
  }, [dispatch, fetchStoreRecommendation]);

  // Helper function for MapleStory SEA world icons (duplicated for this component)
  const getVesuviusStoreIcon = (storeCode) => {
    // Legacy tree-themed icons (preserved for rollback)
    /*
    const vesuviusIconMap = {
      terra: "🌳", // Earth tree - Oak/Strong tree
      aqua: "🌊", // Water - Keep wave for water association
      ignis: "🍁", // Fire - Autumn leaves (fiery colors)
      ventus: "🌿", // Wind - Leaves in the wind
      silva: "🌲", // Forest - Pine/Evergreen tree
    };
    */

    // MapleStory SEA world icons (constellation-themed)
    const mapleWorldIconMap = {
      aquila: "/girl_animation_2/stand1_0.png", // Eagle constellation - girl character sprite
      bootes: "✨", // Herdsman constellation - sparkling star for the guardian
      cassiopeia: "⋆", // Queen constellation - elegant star for royalty
      delphinus: "🌟", // Dolphin constellation - glowing star of the seas
      eridanus: "💫", // River constellation - flowing shooting star
    };

    return mapleWorldIconMap[storeCode?.toLowerCase()] || "✦"; // Default: Four-pointed star
  };

  useEffect(() => {
    if (!sessionToken || !userProfile) {
      navigate(PATH_HOME, { replace: true });
      triggerAlert("Authentication", "Please login again.");
      return;
    }

    fetchStores();
    initializeStoreSelection();
  }, [
    sessionToken,
    userProfile,
    fetchStores,
    initializeStoreSelection,
    navigate,
    triggerAlert,
  ]);

  useEffect(() => {
    webApp.ready();
    webApp.BackButton.onClick(onBackClick);
    webApp.BackButton.show();
    webApp.MainButton.hide();

    return () => {
      webApp.BackButton.offClick(onBackClick);
    };
  }, [webApp, onBackClick]);

  // Error retry handler
  const handleRetry = useCallback(() => {
    if (error?.action === "fetchStores") {
      fetchStores();
    } else if (error?.action === "storeSelect") {
      // For store selection errors, just clear the error and let user try again
      setError(null);
    } else if (error?.action === "mainClick") {
      // For main click errors, clear error and try again
      setError(null);
      onMainClick();
    } else {
      // Generic retry: reload the page data
      fetchStores();
      initializeStoreSelection();
    }
  }, [error, fetchStores, initializeStoreSelection, onMainClick]);

  // Don't render if not authenticated (auth guard will handle redirect)
  if (!isAuthenticated) {
    return <Loader title="Authenticating..." />;
  }

  // Show error screen if there's an error
  if (error) {
    return (
      <ErrorHandler
        error={error}
        title={error.title || "Store Error"}
        subtitle={error.subtitle || "Something went wrong"}
        onRetry={handleRetry}
        showReturnButton={true}
      />
    );
  }

  return (
    <TelegramScreen className={"store-selection-container"}>
      {!isLoading ? (
        <>
          {stores?.length > 0 ? (
            // Show store selection tiles
            <>
              <TelegramHeader>
                <TelegramText className={"telegramTitle"}>
                  SELECT WORLD
                </TelegramText>
                <TelegramText
                  className={"telegramSubtitle"}
                  style={{ textAlign: "center" }}
                >
                  Choose your world
                </TelegramText>
              </TelegramHeader>
              
              {/* Store Balance Recommendation Banner */}
              {storeRecommendation?.recommendation?.shouldShow && (
                <div className="store-recommendation-banner">
                  <TelegramText className="recommendation-message">
                    ✨ {storeRecommendation.recommendation.message}
                  </TelegramText>
                </div>
              )}
              
              <div className="oneline__cards__container">
                {stores.map((store) => {
                  // Check if this store is recommended (no order count exposure)
                  const isRecommended = storeRecommendation?.recommendation?.shouldShow && 
                    storeRecommendation.recommendation?.recommendedStoreId === store.id;
                  
                  return (
                    <ItemButton
                      item={store}
                      key={store.id}
                      handleButtonClick={onStoreSelect}
                      type="store"
                      disabled={selectedStore?.id === store.id}
                      isRecommended={isRecommended}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <TelegramHeader>
                <TelegramText className={"telegramTitle"}>
                  No stores available
                </TelegramText>
                <TelegramText className={"telegramSubtitle"}>
                  Please contact support
                </TelegramText>
              </TelegramHeader>
            </>
          )}
        </>
      ) : (
        <Loader title="Connecting to worlds..." />
      )}
    </TelegramScreen>
  );
};

export default StoreSelectionScreen;
